import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useStorage } from '../../hooks/useStorage';
import supabase from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import ImageWithFallback from '../../components/ImageWithFallback';
import PlayerSponsorAdminPanel from '../../components/admin/PlayerSponsorAdminPanel';
import SponsorshipRequestsAdmin from '../../components/admin/SponsorshipRequestsAdmin';
import * as FiIcons from 'react-icons/fi';

const {
  FiSave,
  FiArrowLeft,
  FiUpload,
  FiAlertCircle,
  FiEdit,
  FiTrash2,
  FiPlus,
  FiX,
  FiArrowUp,
  FiArrowDown,
  FiExternalLink,
  FiShield,
  FiUsers,
  FiHeart,
  FiTag,
} = FiIcons;

const tabs = [
  { id: 'club', label: 'Χορηγοί Ομάδας', icon: FiShield },
  { id: 'players', label: 'Χορηγοί Παικτριών', icon: FiUsers },
  { id: 'requests', label: 'Ενδιαφέρον Χορηγίας', icon: FiHeart },
];

const slugify = (value = '') =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || `category-${Date.now()}`;

const Sponsors = () => {
  const [activeTab, setActiveTab] = useState('club');
  const [sponsors, setSponsors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [newCategory, setNewCategory] = useState('');
  const { uploadFile, uploading, error: uploadError } = useStorage();
  const navigate = useNavigate();
  const formRef = useRef(null);

  const defaultType = categories.find((c) => c.is_active !== false)?.slug || 'main';
  const [formData, setFormData] = useState({ name: '', image_url: '', url: '', type: 'main', sort_order: 0 });
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError(null);
      const [{ data: sponsorRows, error: sponsorError }, { data: categoryRows, error: categoryError }] = await Promise.all([
        supabase.from('sponsors_aox1234').select('*').order('sort_order', { ascending: true }).order('name', { ascending: true }),
        supabase.from('sponsor_categories_aox1234').select('*').order('sort_order', { ascending: true }).order('name', { ascending: true }),
      ]);
      if (sponsorError) throw sponsorError;
      if (categoryError) throw categoryError;
      setSponsors(sponsorRows || []);
      setCategories(categoryRows || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    if (showForm) requestAnimationFrame(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, [showForm, editingId]);

  const resetForm = () => {
    setFormData({ name: '', image_url: '', url: '', type: defaultType, sort_order: 0 });
    setEditingId(null);
    setShowForm(false);
    setImageFile(null);
    setPreviewUrl('');
  };

  const openCreateForm = () => {
    setEditingId(null);
    setFormData({ name: '', image_url: '', url: '', type: defaultType, sort_order: sponsors.length });
    setImageFile(null);
    setPreviewUrl('');
    setMessage({ type: '', text: '' });
    setShowForm(true);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'Παρακαλώ ανεβάστε JPG, PNG, GIF, WebP ή SVG.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Το αρχείο πρέπει να είναι μικρότερο από 5MB.' });
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreviewUrl(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage({ type: '', text: '' });
    try {
      if (!formData.name.trim()) throw new Error('Το όνομα χορηγού είναι υποχρεωτικό.');
      if (!formData.type) throw new Error('Επιλέξτε κατηγορία χορηγού.');
      let imageUrl = formData.image_url;
      if (imageFile) {
        const uploadedUrl = await uploadFile(imageFile, 'team_logos', 'sponsors/');
        if (!uploadedUrl) throw new Error(uploadError || 'Αποτυχία μεταφόρτωσης εικόνας');
        imageUrl = uploadedUrl;
      }
      const sponsorData = {
        name: formData.name.trim(),
        image_url: imageUrl || null,
        url: formData.url.trim() || null,
        type: formData.type,
        sort_order: Number.isNaN(parseInt(formData.sort_order, 10)) ? 0 : parseInt(formData.sort_order, 10),
        updated_at: new Date().toISOString(),
      };
      const result = editingId
        ? await supabase.from('sponsors_aox1234').update(sponsorData).eq('id', editingId)
        : await supabase.from('sponsors_aox1234').insert([{ ...sponsorData, created_at: new Date().toISOString() }]);
      if (result.error) throw result.error;
      setMessage({ type: 'success', text: editingId ? 'Ο χορηγός ενημερώθηκε.' : 'Ο νέος χορηγός προστέθηκε.' });
      resetForm();
      await fetchAll();
    } catch (err) {
      setMessage({ type: 'error', text: `Σφάλμα: ${err.message}` });
    }
  };

  const handleEdit = (sponsor) => {
    setFormData({ name: sponsor.name || '', image_url: sponsor.image_url || '', url: sponsor.url || '', type: sponsor.type || defaultType, sort_order: sponsor.sort_order ?? 0 });
    setEditingId(sponsor.id);
    setShowForm(true);
    setImageFile(null);
    setPreviewUrl('');
    setMessage({ type: '', text: '' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Να διαγραφεί αυτός ο χορηγός;')) return;
    const { error: deleteError } = await supabase.from('sponsors_aox1234').delete().eq('id', id);
    if (deleteError) setMessage({ type: 'error', text: `Σφάλμα: ${deleteError.message}` });
    else { setMessage({ type: 'success', text: 'Ο χορηγός διαγράφηκε.' }); await fetchAll(); }
  };

  const swapOrder = async (sponsor, direction) => {
    const sorted = [...sponsors].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const currentIndex = sorted.findIndex((item) => item.id === sponsor.id);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (currentIndex < 0 || targetIndex < 0 || targetIndex >= sorted.length) return;
    const target = sorted[targetIndex];
    const currentOrder = sponsor.sort_order ?? currentIndex;
    const targetOrder = target.sort_order ?? targetIndex;
    const first = await supabase.from('sponsors_aox1234').update({ sort_order: targetOrder }).eq('id', sponsor.id);
    const second = await supabase.from('sponsors_aox1234').update({ sort_order: currentOrder }).eq('id', target.id);
    if (first.error || second.error) setMessage({ type: 'error', text: 'Αποτυχία αλλαγής σειράς.' }); else await fetchAll();
  };

  const addCategory = async () => {
    const name = newCategory.trim();
    if (!name) return;
    const { error: insertError } = await supabase.from('sponsor_categories_aox1234').insert({ name, slug: slugify(name), sort_order: categories.length, is_active: true, updated_at: new Date().toISOString() });
    if (insertError) setMessage({ type: 'error', text: `Σφάλμα κατηγορίας: ${insertError.message}` });
    else { setNewCategory(''); await fetchAll(); }
  };

  const updateCategory = async (category, changes) => {
    const { error: updateError } = await supabase.from('sponsor_categories_aox1234').update({ ...changes, updated_at: new Date().toISOString() }).eq('id', category.id);
    if (updateError) setMessage({ type: 'error', text: `Σφάλμα κατηγορίας: ${updateError.message}` }); else await fetchAll();
  };

  const deleteCategory = async (category) => {
    if (sponsors.some((s) => s.type === category.slug)) {
      setMessage({ type: 'error', text: `Η κατηγορία «${category.name}» χρησιμοποιείται από χορηγούς. Αλλάξτε πρώτα την κατηγορία τους.` });
      return;
    }
    if (!window.confirm(`Να διαγραφεί η κατηγορία «${category.name}»;`)) return;
    const { error: deleteError } = await supabase.from('sponsor_categories_aox1234').delete().eq('id', category.id);
    if (deleteError) setMessage({ type: 'error', text: `Σφάλμα κατηγορίας: ${deleteError.message}` }); else await fetchAll();
  };

  const typeLabel = (value) => categories.find((c) => c.slug === value)?.name || value;
  const activeCategories = categories.filter((c) => c.is_active !== false);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="sticky top-0 z-30 border-b border-gray-100 bg-white shadow-sm">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => navigate('/admin/dashboard')} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"><SafeIcon icon={FiArrowLeft} className="h-5 w-5" /></button>
              <div><h1 className="text-lg font-black text-gray-900">Χορηγίες</h1><p className="text-xs text-gray-500">Ομάδα · Παίκτριες · Ενδιαφέρον χορηγών</p></div>
            </div>
            {activeTab === 'club' && <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => setShowCategories((v) => !v)} className="inline-flex items-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 font-bold text-gray-700 hover:bg-gray-50"><SafeIcon icon={FiTag} className="mr-2 h-5 w-5" />Κατηγορίες</button>
              <button type="button" onClick={showForm ? resetForm : openCreateForm} className={`inline-flex items-center rounded-xl px-4 py-2.5 font-bold text-white ${showForm ? 'bg-gray-700' : 'bg-primary-600 hover:bg-primary-700'}`}><SafeIcon icon={showForm ? FiX : FiPlus} className="mr-2 h-5 w-5" />{showForm ? 'Ακύρωση' : 'Προσθήκη Χορηγού'}</button>
            </div>}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 grid grid-cols-1 gap-2 rounded-2xl border border-gray-100 bg-white p-2 shadow-sm sm:grid-cols-3">
          {tabs.map((tab) => <button key={tab.id} type="button" onClick={() => { setActiveTab(tab.id); setMessage({ type: '', text: '' }); }} className={`flex items-center justify-center rounded-xl px-4 py-3 text-sm font-black ${activeTab === tab.id ? 'bg-primary-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'}`}><SafeIcon icon={tab.icon} className="mr-2 h-4 w-4" />{tab.label}</button>)}
        </div>

        {activeTab === 'players' && <PlayerSponsorAdminPanel />}
        {activeTab === 'requests' && <SponsorshipRequestsAdmin />}

        {activeTab === 'club' && <>
          {message.text && <div className={`mb-6 rounded-xl border p-4 text-sm font-semibold ${message.type === 'success' ? 'border-green-100 bg-green-50 text-green-700' : 'border-red-100 bg-red-50 text-red-700'}`}><SafeIcon icon={FiAlertCircle} className="mr-2 inline h-5 w-5" />{message.text}</div>}
          {error && <div className="mb-6 rounded-xl border border-red-100 bg-red-50 p-4 text-red-700">{error}</div>}

          {showCategories && <section className="mb-8 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4"><h2 className="text-xl font-black text-gray-900">Κατηγορίες Χορηγών</h2><p className="text-sm text-gray-500">Ορίζονται εδώ από τον admin και μετά επιλέγονται στη φόρμα χορηγού.</p></div>
            <div className="mb-5 flex flex-col gap-2 sm:flex-row"><input value={newCategory} onChange={(e) => setNewCategory(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCategory(); } }} placeholder="Νέα κατηγορία" className="flex-1 rounded-xl border border-gray-200 px-4 py-3" /><button type="button" onClick={addCategory} className="rounded-xl bg-primary-600 px-4 py-3 font-black text-white"><SafeIcon icon={FiPlus} className="mr-2 inline h-4 w-4" />Προσθήκη</button></div>
            <div className="space-y-2">{categories.map((category) => <div key={category.id} className="flex flex-col gap-2 rounded-xl border border-gray-100 bg-gray-50 p-3 sm:flex-row sm:items-center"><input value={category.name} onChange={(e) => setCategories((prev) => prev.map((c) => c.id === category.id ? { ...c, name: e.target.value } : c))} onBlur={(e) => updateCategory(category, { name: e.currentTarget.value.trim() })} className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 font-bold" /><span className="rounded-lg bg-white px-3 py-2 text-xs text-gray-500">{category.slug}</span><button type="button" onClick={() => updateCategory(category, { is_active: category.is_active === false })} className={`rounded-lg px-3 py-2 text-xs font-black ${category.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-500'}`}>{category.is_active !== false ? 'Ενεργή' : 'Ανενεργή'}</button><button type="button" onClick={() => deleteCategory(category)} className="rounded-lg p-2 text-red-600 hover:bg-red-50"><SafeIcon icon={FiTrash2} className="h-4 w-4" /></button></div>)}</div>
          </section>}

          {showForm && <motion.section ref={formRef} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="scroll-mt-28 mb-8 rounded-2xl border-2 border-primary-100 bg-white p-6 shadow-lg">
            <div className="mb-6 flex items-center justify-between"><h2 className="text-xl font-black text-gray-900">{editingId ? 'Επεξεργασία Χορηγού' : 'Προσθήκη Νέου Χορηγού'}</h2><button type="button" onClick={resetForm} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"><SafeIcon icon={FiX} className="h-5 w-5" /></button></div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-[240px_1fr]">
                <div><label className="mb-2 block text-sm font-black text-gray-800">Λογότυπο</label><div className="flex aspect-[3/2] items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-gray-50 p-4"><ImageWithFallback src={previewUrl || formData.image_url} alt={formData.name} className="max-h-full max-w-full object-contain" /></div><label className="mt-3 flex cursor-pointer items-center justify-center rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-50"><SafeIcon icon={FiUpload} className="mr-2 h-4 w-4" />{imageFile ? 'Αλλαγή εικόνας' : 'Επιλογή εικόνας'}<input type="file" accept="image/*" className="sr-only" onChange={handleImageChange} /></label></div>
                <div className="grid content-start grid-cols-1 gap-5 md:grid-cols-2">
                  <label className="block md:col-span-2"><span className="mb-1.5 block text-sm font-black text-gray-800">Όνομα Χορηγού *</span><input type="text" name="name" required value={formData.name} onChange={handleInputChange} className="w-full rounded-xl border border-gray-200 px-4 py-3" /></label>
                  <label className="block md:col-span-2"><span className="mb-1.5 block text-sm font-black text-gray-800">Website URL</span><input type="url" name="url" value={formData.url} onChange={handleInputChange} placeholder="https://..." className="w-full rounded-xl border border-gray-200 px-4 py-3" /></label>
                  <label className="block"><span className="mb-1.5 block text-sm font-black text-gray-800">Κατηγορία *</span><select name="type" required value={formData.type} onChange={handleInputChange} className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3"><option value="">Επιλέξτε...</option>{activeCategories.map((category) => <option key={category.id} value={category.slug}>{category.name}</option>)}</select></label>
                  <label className="block"><span className="mb-1.5 block text-sm font-black text-gray-800">Σειρά</span><input type="number" name="sort_order" value={formData.sort_order} onChange={handleInputChange} className="w-full rounded-xl border border-gray-200 px-4 py-3" /></label>
                </div>
              </div>
              <div className="flex justify-end gap-3 border-t border-gray-100 pt-5"><button type="button" onClick={resetForm} className="rounded-xl border border-gray-200 px-5 py-3 font-bold text-gray-700">Ακύρωση</button><button type="submit" disabled={uploading} className="inline-flex items-center rounded-xl bg-primary-600 px-5 py-3 font-black text-white disabled:opacity-50"><SafeIcon icon={FiSave} className="mr-2 h-5 w-5" />{uploading ? 'Αποθήκευση...' : 'Αποθήκευση'}</button></div>
            </form>
          </motion.section>}

          <div id="aox-sponsor-intelligence-host" className="mb-8" />

          <section className="rounded-2xl border border-gray-100 bg-white shadow-sm">
            <div className="border-b border-gray-100 px-5 py-4"><h2 className="font-black text-gray-900">Χορηγοί Ομάδας</h2></div>
            {loading ? <div className="p-8 text-center text-gray-500">Φόρτωση...</div> : sponsors.length === 0 ? <div className="p-8 text-center text-gray-500">Δεν υπάρχουν χορηγοί.</div> : <div className="divide-y divide-gray-100">{sponsors.map((sponsor, index) => <div key={sponsor.id} className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center"><div className="flex h-16 w-24 flex-none items-center justify-center rounded-xl bg-gray-50 p-2"><ImageWithFallback src={sponsor.image_url} alt={sponsor.name} className="max-h-full max-w-full object-contain" /></div><div className="min-w-0 flex-1"><div className="font-black text-gray-900">{sponsor.name}</div><div className="mt-1 inline-flex rounded-full bg-primary-50 px-2.5 py-1 text-xs font-bold text-primary-700">{typeLabel(sponsor.type)}</div>{sponsor.url && <a href={sponsor.url} target="_blank" rel="noreferrer" className="ml-3 inline-flex items-center text-xs text-gray-500 hover:text-primary-600">Website <SafeIcon icon={FiExternalLink} className="ml-1 h-3 w-3" /></a>}</div><div className="flex items-center gap-1"><button type="button" onClick={() => swapOrder(sponsor, 'up')} disabled={index === 0} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-30"><SafeIcon icon={FiArrowUp} className="h-4 w-4" /></button><button type="button" onClick={() => swapOrder(sponsor, 'down')} disabled={index === sponsors.length - 1} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-30"><SafeIcon icon={FiArrowDown} className="h-4 w-4" /></button><button type="button" onClick={() => handleEdit(sponsor)} className="rounded-lg p-2 text-primary-600 hover:bg-primary-50"><SafeIcon icon={FiEdit} className="h-4 w-4" /></button><button type="button" onClick={() => handleDelete(sponsor.id)} className="rounded-lg p-2 text-red-600 hover:bg-red-50"><SafeIcon icon={FiTrash2} className="h-4 w-4" /></button></div></div>)}</div>}
          </section>
        </>}
      </div>
    </div>
  );
};

export default Sponsors;
